/**
 * ChatDrawer — floating chat panel containing the full chat UI.
 */
import { useEffect, useState } from 'react';
import { Button, Typography, Space, Tooltip, Empty, Grid, theme } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  HistoryOutlined,
  CloseOutlined,
  ExpandOutlined,
  CompressOutlined,
} from '@ant-design/icons';
import { useChatContext } from '@/context/ChatContext';
import { useChatPageContext } from '@/hooks/useChatPageContext';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { ChatSuggestions } from './ChatSuggestions';

const { Title, Text } = Typography;

export function ChatDrawer() {
  const {
    isOpen,
    closeChat,
    panelMode,
    togglePanelMode,
    sessions,
    activeSessionId,
    setActiveSession,
    createSession,
    deleteSession,
    messages,
    streamingContent,
    isStreaming,
    sendMessage,
  } = useChatContext();

  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { pageContext, contextLabel, suggestions } = useChatPageContext();
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeChat();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeChat]);

  const handleSend = (content: string) => {
    sendMessage(content, pageContext);
    setShowHistory(false);
  };

  const handleSelectSuggestion = (text: string) => {
    sendMessage(text, pageContext);
    setShowHistory(false);
  };

  const handleNewSession = async () => {
    await createSession();
    setShowHistory(false);
  };

  const showSuggestions = messages.length === 0 && !isStreaming && !showHistory;

  if (!isOpen) return null;

  const compactPanelStyle: React.CSSProperties = {
    width: 'min(430px, calc(100vw - 24px))',
    height: 'min(640px, calc(100dvh - 108px))',
    right: 'clamp(12px, 2.2vw, 24px)',
    bottom: 'clamp(16px, 2.2vw, 24px)',
    borderRadius: token.borderRadiusLG,
  };

  const expandedPanelStyle: React.CSSProperties = {
    width: 'min(92vw, 1240px)',
    height: 'min(88dvh, 920px)',
    right: '4vw',
    bottom: '6dvh',
    borderRadius: token.borderRadiusLG,
  };

  const mobilePanelStyle: React.CSSProperties = {
    inset: 0,
    width: '100vw',
    height: '100dvh',
    borderRadius: 0,
  };

  const panelPositionStyle = isMobile
    ? mobilePanelStyle
    : panelMode === 'expanded'
      ? expandedPanelStyle
      : compactPanelStyle;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: token.zIndexPopupBase + 5,
        pointerEvents: 'none',
      }}
      aria-live="polite"
    >
      <section
        data-tour="ai-chat-panel"
        style={{
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          background: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
          overflow: 'hidden',
          pointerEvents: 'auto',
          transition: 'width 0.2s ease, height 0.2s ease, right 0.2s ease, bottom 0.2s ease',
          ...panelPositionStyle,
        }}
      >
        <header
          data-tour="ai-chat-header"
          style={{
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: token.colorBgContainer,
          }}
        >
          <Space>
            <Title level={5} style={{ margin: 0, letterSpacing: '0.02em' }}>Delfos</Title>
            {contextLabel && (
              <Text type="secondary" style={{ fontSize: 11 }}>• {contextLabel}</Text>
            )}
          </Space>
          <Space>
            <Tooltip title="Histórico de conversas">
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => setShowHistory(v => !v)}
              />
            </Tooltip>
            {!isMobile && (
              <Tooltip title={panelMode === 'expanded' ? 'Modo compacto' : 'Ampliar'}>
                <Button
                  type="text"
                  size="small"
                  icon={panelMode === 'expanded' ? <CompressOutlined /> : <ExpandOutlined />}
                  onClick={togglePanelMode}
                  data-tour="ai-chat-toggle-size"
                />
              </Tooltip>
            )}
            <Tooltip title="Nova conversa">
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleNewSession}
              />
            </Tooltip>
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={closeChat} />
          </Space>
        </header>

      {/* History panel */}
      {showHistory && (
        <div
          style={{
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            maxHeight: 220,
            overflowY: 'auto',
            background: token.colorBgContainer,
          }}
        >
          {sessions.length === 0 ? (
            <Empty description="Nenhuma conversa" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 16 }} />
          ) : (
            sessions.map((s) => (
              <div
                key={s.id}
                onClick={() => { setActiveSession(s.id); setShowHistory(false); }}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  background: s.id === activeSessionId ? token.colorPrimaryBg : 'transparent',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <Text style={{ fontSize: 12, maxWidth: 300 }} ellipsis>
                  {s.title ?? `Conversa #${s.id}`}
                </Text>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                />
              </div>
            ))
          )}
        </div>
      )}

      {/* Message list */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {messages.length === 0 && !isStreaming && !showHistory ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
            <svg viewBox="0 0 56 56" width="48" height="48" aria-hidden="true" style={{ marginBottom: 12, opacity: 0.9 }}>
              <defs>
                <radialGradient id="dlWelcomeIris" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#d0e8ff" />
                  <stop offset="100%" stopColor="#1f4bd8" />
                </radialGradient>
              </defs>
              <circle cx="28" cy="28" r="26" fill="#1f4bd8" opacity="0.12" />
              <path d="M6 28 Q28 10 50 28 Q28 46 6 28 Z" fill="none" stroke="#1f4bd8" strokeWidth="1.5" opacity="0.7" />
              <circle cx="28" cy="28" r="11" fill="url(#dlWelcomeIris)" />
              <circle cx="28" cy="28" r="7" fill="#1233a6" />
              <ellipse cx="31" cy="24" rx="2.5" ry="1.5" fill="rgba(255,255,255,0.5)" transform="rotate(-20 31 24)" />
            </svg>
            <Title level={5} style={{ marginTop: 0, marginBottom: 4, letterSpacing: '0.03em', color: token.colorPrimary }}>Delfos</Title>
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
              Oráculo PMO — consulte o portfólio, projetos, riscos e marcos de {contextLabel}.
            </Text>
          </div>
        ) : (
          <ChatMessageList
            messages={messages}
            streamingContent={streamingContent}
            isStreaming={isStreaming}
          />
        )}
      </div>

      {/* Suggestions */}
      {showSuggestions && (
        <ChatSuggestions suggestions={suggestions} onSelect={handleSelectSuggestion} />
      )}

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        disabled={isStreaming}
        placeholder={`Pergunte sobre ${contextLabel}…`}
      />
      </section>
    </div>
  );
}
